import Phaser from 'phaser'
import {
  GAME_WIDTH,
  GAME_HEIGHT,
  PLAYER_CONFIG,
  ALIEN_CONFIG,
  BULLET_CONFIG,
  UI_CONFIG,
  POWERUP_CONFIG,
} from '../config/game.config'
import { audioManager } from '../audio/AudioManager'
import { accessibilityAnnouncer } from '../accessibility/AccessibilityAnnouncer'
import { TouchControlsManager } from '../controls/TouchControlsManager'
import { shouldUseTouchControls } from '../utils/deviceDetection'
import { LeaderboardManager } from '../utils/LeaderboardManager'
import { createGameOverLeaderboard } from '../ui/LeaderboardDisplay'
import { difficultyManager } from '../difficulty'
import { eventManager, eventThemeManager } from '../events'
import type { SeasonalEvent, EventAchievement } from '../events'
import { PowerUpManager, PowerUpType } from '../powerups'
import type { PowerUpConfig, PowerUpSprite, ActivePowerUp } from '../powerups'

interface Invader extends Phaser.GameObjects.Sprite {
  row: number
  col: number
  points: number
  invaderType: string
  animFrame: number
}

export class GameScene extends Phaser.Scene {
  // Game objects
  private player!: Phaser.GameObjects.Sprite
  private aliens!: Phaser.GameObjects.Group
  private playerBullets!: Phaser.GameObjects.Group
  private alienBullets!: Phaser.GameObjects.Group

  // Input
  private cursors!: Phaser.Types.Input.Keyboard.CursorKeys
  private fireKey!: Phaser.Input.Keyboard.Key
  private escKey!: Phaser.Input.Keyboard.Key
  private touchControls?: TouchControlsManager
  private useTouchControls = false

  // Game state
  private score = 0
  private lives = PLAYER_CONFIG.startLives
  private isGameOver = false
  private isPaused = false
  private canShoot = true
  private isPlayerInvincible = false
  private globalRank: number | null = null

  // Alien movement
  private alienDirection = 1 // 1 = right, -1 = left
  private alienSpeed = ALIEN_CONFIG.initialSpeed
  private lastAlienShootTime = 0
  private lastAnimationTime = 0
  private currentAnimFrame = 1

  // UI elements
  private scoreText!: Phaser.GameObjects.Text
  private livesText!: Phaser.GameObjects.Text
  private muteButton!: Phaser.GameObjects.Text
  private pauseOverlay?: Phaser.GameObjects.Container

  // Event system
  private currentEvent: SeasonalEvent | null = null
  private eventBannerText?: Phaser.GameObjects.Text
  private eventCountdownText?: Phaser.GameObjects.Text
  private achievementToast?: Phaser.GameObjects.Container
  private countdownTimer?: Phaser.Time.TimerEvent
  private aliensDestroyedThisGame = 0

  // Power-up system
  private powerUpManager!: PowerUpManager
  private powerUpUIContainer?: Phaser.GameObjects.Container
  private powerUpTimerTexts: Map<PowerUpType, Phaser.GameObjects.Text> = new Map()
  private shieldGraphics?: Phaser.GameObjects.Graphics

  constructor() {
    super({ key: 'GameScene' })
  }

  create(): void {
    this.resetGameState()
    this.initAudio()
    this.initEventSystem()
    this.initPowerUpSystem()
    this.createPlayer()
    this.createAlienGrid()
    this.createBulletGroups()
    this.setupInput()
    this.setupTouchControls()
    this.setupCollisions()
    this.createUI()
    this.createEventUI()
    this.createPowerUpUI()
    this.announceGameStart()

    // Record game start for event progress
    eventManager.onGameStart()

    // Start leaderboard game session for score validation
    LeaderboardManager.startGame()
  }

  private async initAudio(): Promise<void> {
    await audioManager.init()
    await audioManager.resume()
    audioManager.resetMarchSequence()
  }

  update(time: number, _delta: number): void {
    if (this.isGameOver || this.isPaused) return

    // Update difficulty manager time tracking
    difficultyManager.updateTime()

    this.handlePlayerMovement()
    this.handlePlayerShooting()
    this.updateAlienMovement()
    this.updateInvaderAnimation(time)
    this.handleAlienShooting(time)
    this.cleanupBullets()
    this.checkWinCondition()

    // Update power-up system
    this.powerUpManager.update()
    this.updatePowerUpUI()
    this.updateShieldVisual()
  }

  private resetGameState(): void {
    this.score = 0
    this.lives = PLAYER_CONFIG.startLives
    this.isGameOver = false
    this.isPaused = false
    this.canShoot = true
    this.isPlayerInvincible = false
    this.globalRank = null
    this.alienDirection = 1
    this.lastAlienShootTime = 0
    this.lastAnimationTime = 0
    this.currentAnimFrame = 1
    this.aliensDestroyedThisGame = 0

    // Initialize difficulty manager for new game
    difficultyManager.resetForNewGame()
    this.alienSpeed = difficultyManager.getInitialAlienSpeed()

    // Clean up power-up state
    this.powerUpTimerTexts.clear()
  }

  private initEventSystem(): void {
    // Get current event from manager
    this.currentEvent = eventManager.getCurrentEvent()

    // Subscribe to achievement unlocks
    eventManager.onAchievementUnlock((achievement) => {
      this.showAchievementToast(achievement)
    })
  }

  private initPowerUpSystem(): void {
    this.powerUpManager = new PowerUpManager(this)

    // Set up callbacks for power-up events
    this.powerUpManager.setOnCollected((_type: PowerUpType, config: PowerUpConfig) => {
      // Show pickup indicator
      this.showPowerUpIndicator(config)
    })

    this.powerUpManager.setOnActivated((type: PowerUpType, _config: PowerUpConfig) => {
      // Announce to screen readers
      const typeConfig = POWERUP_CONFIG.types[type]
      accessibilityAnnouncer.announcePolite(`${typeConfig.name} activated`)
    })

    this.powerUpManager.setOnDeactivated((type: PowerUpType) => {
      const typeConfig = POWERUP_CONFIG.types[type]
      accessibilityAnnouncer.announcePolite(`${typeConfig.name} expired`)
    })
  }

  private showPowerUpIndicator(config: PowerUpConfig): void {
    // Show floating text when power-up collected
    const text = this.add.text(
      GAME_WIDTH / 2,
      GAME_HEIGHT / 2 - 50,
      `${config.icon} ${config.name}`,
      {
        fontFamily: 'monospace',
        fontSize: '24px',
        color: '#ffffff',
        stroke: '#000000',
        strokeThickness: 4,
      }
    ).setOrigin(0.5)

    this.tweens.add({
      targets: text,
      y: GAME_HEIGHT / 2 - 100,
      alpha: 0,
      scale: 1.5,
      duration: 1000,
      ease: 'Power2',
      onComplete: () => text.destroy(),
    })
  }

  // ========================================
  // Player Setup and Movement
  // ========================================

  private createPlayer(): void {
    this.player = this.add.sprite(
      GAME_WIDTH / 2,
      GAME_HEIGHT - 50,
      'player'
    )

    this.physics.add.existing(this.player)
    const playerBody = this.player.body as Phaser.Physics.Arcade.Body
    playerBody.setCollideWorldBounds(true)
    playerBody.setSize(PLAYER_CONFIG.width, PLAYER_CONFIG.height)
  }

  private handlePlayerMovement(): void {
    if (!this.player.active) return

    const playerBody = this.player.body as Phaser.Physics.Arcade.Body
    playerBody.setVelocityX(0)

    // Touch controls take priority
    if (this.useTouchControls && this.touchControls) {
      const direction = this.touchControls.getMovementDirection()
      if (direction !== 0) {
        playerBody.setVelocityX(direction * PLAYER_CONFIG.speed)
        return
      }
    }

    // Keyboard controls as fallback
    if (this.cursors) {
      if (this.cursors.left.isDown) {
        playerBody.setVelocityX(-PLAYER_CONFIG.speed)
      } else if (this.cursors.right.isDown) {
        playerBody.setVelocityX(PLAYER_CONFIG.speed)
      }
    }
  }

  // ========================================
  // Alien Grid Formation and Movement
  // ========================================

  private createAlienGrid(): void {
    this.aliens = this.add.group()

    // Get invader types from theme manager (uses themed or default)
    const invaderTypes = eventThemeManager.getInvaderTypes()
    const rowTypes = eventThemeManager.getRowTypes()

    for (let row = 0; row < ALIEN_CONFIG.rows; row++) {
      for (let col = 0; col < ALIEN_CONFIG.cols; col++) {
        const x = ALIEN_CONFIG.startX + col * ALIEN_CONFIG.horizontalSpacing
        const y = ALIEN_CONFIG.startY + row * ALIEN_CONFIG.verticalSpacing

        // Get invader type for this row
        const typeIndex = rowTypes[row]
        const invaderType = invaderTypes[typeIndex]
        const textureKey = `${invaderType.key}_1`

        const invader = this.add.sprite(x, y, textureKey) as Invader

        invader.row = row
        invader.col = col
        invader.points = invaderType.points
        invader.invaderType = invaderType.key
        invader.animFrame = 1

        this.physics.add.existing(invader)
        const invaderBody = invader.body as Phaser.Physics.Arcade.Body
        invaderBody.setSize(invaderType.width, invaderType.height)

        this.aliens.add(invader)
      }
    }
  }

  private updateAlienMovement(): void {
    const invaders = this.aliens.getChildren() as Invader[]
    if (invaders.length === 0) return

    // Find the bounds of the alien formation
    let leftMost = GAME_WIDTH
    let rightMost = 0

    for (const invader of invaders) {
      if (invader.active) {
        leftMost = Math.min(leftMost, invader.x - ALIEN_CONFIG.width / 2)
        rightMost = Math.max(rightMost, invader.x + ALIEN_CONFIG.width / 2)
      }
    }

    // Check if we need to change direction
    let shouldDrop = false
    if (this.alienDirection === 1 && rightMost >= GAME_WIDTH - 20) {
      this.alienDirection = -1
      shouldDrop = true
    } else if (this.alienDirection === -1 && leftMost <= 20) {
      this.alienDirection = 1
      shouldDrop = true
    }

    // Get slow motion multiplier from power-ups
    const slowMotionMultiplier = this.powerUpManager.getAlienSpeedMultiplier()

    // Move all invaders
    for (const invader of invaders) {
      if (invader.active) {
        invader.x += this.alienDirection * this.alienSpeed * slowMotionMultiplier * 0.016 // Assuming ~60fps
        if (shouldDrop) {
          invader.y += ALIEN_CONFIG.dropDistance * slowMotionMultiplier

          // Check if aliens reached the player level (game over)
          if (invader.y >= GAME_HEIGHT - 100) {
            this.gameOver()
            return
          }
        }
      }
    }

    // Increase speed as aliens are destroyed, starting from difficulty-adjusted base
    const remainingInvaders = invaders.filter((a) => a.active).length
    const totalAliens = ALIEN_CONFIG.rows * ALIEN_CONFIG.cols
    const destroyedRatio = 1 - remainingInvaders / totalAliens
    const baseSpeed = difficultyManager.getInitialAlienSpeed()
    this.alienSpeed =
      baseSpeed +
      destroyedRatio * ALIEN_CONFIG.speedIncrease * 10
  }

  private updateInvaderAnimation(time: number): void {
    // Calculate dynamic animation speed based on remaining invaders
    // Speed up as invaders are destroyed (matches the original game behavior)
    const invaders = this.aliens.getChildren() as Invader[]
    const remainingInvaders = invaders.filter((a) => a.active).length
    const totalAliens = ALIEN_CONFIG.rows * ALIEN_CONFIG.cols
    const destroyedRatio = 1 - remainingInvaders / totalAliens

    // Animation speed decreases from 500ms to ~100ms as invaders are destroyed
    const dynamicAnimSpeed = ALIEN_CONFIG.animationSpeed * (1 - destroyedRatio * 0.8)

    // Toggle animation frames at dynamic interval
    if (time - this.lastAnimationTime > dynamicAnimSpeed) {
      this.lastAnimationTime = time
      this.currentAnimFrame = this.currentAnimFrame === 1 ? 2 : 1

      // Play the classic march "thump" sound with each animation toggle
      if (remainingInvaders > 0) {
        audioManager.playMarchSound()
      }

      for (const invader of invaders) {
        if (invader.active) {
          const textureKey = `${invader.invaderType}_${this.currentAnimFrame}`
          invader.setTexture(textureKey)
        }
      }
    }
  }

  // ========================================
  // Shooting Mechanics
  // ========================================

  private createBulletGroups(): void {
    this.playerBullets = this.add.group()
    this.alienBullets = this.add.group()
  }

  private setupInput(): void {
    if (this.input.keyboard) {
      this.cursors = this.input.keyboard.createCursorKeys()
      this.fireKey = this.input.keyboard.addKey(
        Phaser.Input.Keyboard.KeyCodes.SPACE
      )
      this.escKey = this.input.keyboard.addKey(
        Phaser.Input.Keyboard.KeyCodes.ESC
      )
      this.escKey.on('down', () => this.togglePause())
    }
  }

  private setupTouchControls(): void {
    // Detect if touch controls should be used
    this.useTouchControls = shouldUseTouchControls()

    if (this.useTouchControls) {
      this.touchControls = new TouchControlsManager(this)
      this.touchControls.create()

      // Handle resize to reposition touch controls
      this.scale.on('resize', () => {
        if (this.touchControls) {
          this.touchControls.updatePosition()
        }
      })
    }
  }

  private handlePlayerShooting(): void {
    if (!this.canShoot || !this.player.active) return

    // Check touch controls first
    if (this.useTouchControls && this.touchControls) {
      if (this.touchControls.isFireButtonPressed()) {
        this.shootPlayerBullet()
        return
      }
    }

    // Keyboard controls as fallback
    if (this.fireKey && this.fireKey.isDown) {
      this.shootPlayerBullet()
    }
  }

  private shootPlayerBullet(): void {
    this.canShoot = false

    // Track shot fired for difficulty analysis
    difficultyManager.recordShotFired()

    // Play shooting sound
    audioManager.playShootSound()

    // Get power-up effects
    const bulletCount = this.powerUpManager.getBulletCount()
    const spreadAngle = this.powerUpManager.getBulletSpreadAngle()

    // Calculate bullet spawn positions for multi-shot
    const bulletStartY = this.player.y - PLAYER_CONFIG.height / 2 - 10

    for (let i = 0; i < bulletCount; i++) {
      const bullet = this.add.rectangle(
        this.player.x,
        bulletStartY,
        BULLET_CONFIG.playerBullet.width,
        BULLET_CONFIG.playerBullet.height,
        BULLET_CONFIG.playerBullet.color
      )

      this.physics.add.existing(bullet)
      const bulletBody = bullet.body as Phaser.Physics.Arcade.Body

      // Calculate velocity based on spread
      if (bulletCount > 1) {
        // Distribute bullets evenly across the spread angle
        const angleOffset = spreadAngle * (i - (bulletCount - 1) / 2) / ((bulletCount - 1) / 2 || 1)
        const vx = Math.sin(angleOffset) * BULLET_CONFIG.playerBullet.speed
        const vy = -Math.cos(angleOffset) * BULLET_CONFIG.playerBullet.speed
        bulletBody.setVelocity(vx, vy)
      } else {
        bulletBody.setVelocityY(-BULLET_CONFIG.playerBullet.speed)
      }

      this.playerBullets.add(bullet)
    }

    // Cooldown timer using dynamic difficulty with power-up modifier
    const baseCooldown = difficultyManager.getPlayerBulletCooldown()
    const cooldownMultiplier = this.powerUpManager.getBulletCooldownMultiplier()
    const actualCooldown = baseCooldown * cooldownMultiplier

    this.time.delayedCall(actualCooldown, () => {
      this.canShoot = true
    })
  }

  private handleAlienShooting(time: number): void {
    const invaders = this.aliens.getChildren().filter((a) => a.active) as Invader[]
    if (invaders.length === 0) return

    // Get dynamic shoot interval based on difficulty
    const shootIntervalRange = difficultyManager.getAlienShootInterval()
    const shootInterval = Phaser.Math.Between(
      shootIntervalRange.min,
      shootIntervalRange.max
    )

    if (time - this.lastAlienShootTime > shootInterval) {
      this.lastAlienShootTime = time

      // Find bottom-most invaders in each column
      const bottomInvaders = this.getBottomInvaders(invaders)
      if (bottomInvaders.length > 0) {
        // Random bottom invader shoots
        const shooter =
          bottomInvaders[Phaser.Math.Between(0, bottomInvaders.length - 1)]
        this.shootAlienBullet(shooter)
      }
    }
  }

  private getBottomInvaders(invaders: Invader[]): Invader[] {
    const columnBottoms: Map<number, Invader> = new Map()

    for (const invader of invaders) {
      if (invader.active) {
        const currentBottom = columnBottoms.get(invader.col)
        if (!currentBottom || invader.y > currentBottom.y) {
          columnBottoms.set(invader.col, invader)
        }
      }
    }

    return Array.from(columnBottoms.values())
  }

  private shootAlienBullet(invader: Invader): void {
    const bullet = this.add.rectangle(
      invader.x,
      invader.y + ALIEN_CONFIG.height / 2 + 5,
      BULLET_CONFIG.alienBullet.width,
      BULLET_CONFIG.alienBullet.height,
      BULLET_CONFIG.alienBullet.color
    )

    this.physics.add.existing(bullet)
    const bulletBody = bullet.body as Phaser.Physics.Arcade.Body
    // Use dynamic alien bullet speed based on difficulty
    bulletBody.setVelocityY(difficultyManager.getAlienBulletSpeed())

    this.alienBullets.add(bullet)
  }

  private cleanupBullets(): void {
    // Remove bullets that have left the screen
    for (const bullet of this.playerBullets.getChildren()) {
      const b = bullet as Phaser.GameObjects.Rectangle
      if (b.y < -20) {
        b.destroy()
      }
    }

    for (const bullet of this.alienBullets.getChildren()) {
      const b = bullet as Phaser.GameObjects.Rectangle
      if (b.y > GAME_HEIGHT + 20) {
        b.destroy()
      }
    }
  }

  // ========================================
  // Collision Detection
  // ========================================

  private setupCollisions(): void {
    // Player bullets hit aliens
    this.physics.add.overlap(
      this.playerBullets,
      this.aliens,
      this.onBulletHitAlien,
      undefined,
      this
    )

    // Alien bullets hit player
    this.physics.add.overlap(
      this.alienBullets,
      this.player,
      this.onBulletHitPlayer,
      undefined,
      this
    )

    // Aliens collide with player (instant death)
    this.physics.add.overlap(
      this.aliens,
      this.player,
      this.onAlienHitPlayer,
      undefined,
      this
    )

    // Player collects power-ups
    this.physics.add.overlap(
      this.powerUpManager.getGroup(),
      this.player,
      this.onPowerUpCollected,
      undefined,
      this
    )
  }

  private onPowerUpCollected(
    powerUpObj: Phaser.Types.Physics.Arcade.GameObjectWithBody | Phaser.Tilemaps.Tile,
    _playerObj: Phaser.Types.Physics.Arcade.GameObjectWithBody | Phaser.Tilemaps.Tile
  ): void {
    const powerUp = powerUpObj as PowerUpSprite
    if (!powerUp.active) return
    this.powerUpManager.collect(powerUp)

    // Track for score validation
    LeaderboardManager.recordPowerUpCollected()
  }

  private onBulletHitAlien(
    bulletObj: Phaser.Types.Physics.Arcade.GameObjectWithBody | Phaser.Tilemaps.Tile,
    invaderObj: Phaser.Types.Physics.Arcade.GameObjectWithBody | Phaser.Tilemaps.Tile
  ): void {
    const bullet = bulletObj as Phaser.GameObjects.Rectangle
    const invader = invaderObj as Invader

    if (!bullet.active || !invader.active) return

    bullet.destroy()
    invader.destroy()

    // Calculate points with event bonus multiplier
    const basePoints = invader.points
    const bonusPoints = eventManager.calculateBonusScore(basePoints, invader.invaderType)

    // Apply power-up score multiplier
    const powerUpMultiplier = this.powerUpManager.getScoreMultiplier()
    const points = Math.floor(bonusPoints * powerUpMultiplier)

    this.score += points
    this.updateScoreUI()

    // Track for event progress
    this.aliensDestroyedThisGame++
    eventManager.onAlienDestroyed(invader.invaderType)
    eventManager.onPointsEarned(points)

    // Track for score validation
    LeaderboardManager.recordAlienDestroyed()

    // Track hit for difficulty analysis
    difficultyManager.recordShotHit(points)

    // Announce score change to screen readers (show bonus if different)
    if (bonusPoints > basePoints) {
      accessibilityAnnouncer.announceScoreChange(this.score, points)
    } else {
      accessibilityAnnouncer.announceScoreChange(this.score, points)
    }
    accessibilityAnnouncer.updateGameState(this.score, this.lives)

    // Audio and visual feedback
    audioManager.playExplosionSound()
    this.createExplosion(invader.x, invader.y)

    // Show bonus indicator if event bonus or power-up multiplier was applied
    if ((bonusPoints > basePoints && this.currentEvent) || powerUpMultiplier > 1) {
      this.showBonusIndicator(invader.x, invader.y, points, basePoints)
    }

    // Try to spawn a power-up
    this.powerUpManager.trySpawn(invader.x, invader.y)
  }

  private showBonusIndicator(x: number, y: number, bonusPoints: number, _basePoints: number): void {
    const bonusText = this.add.text(x, y - 20, `+${bonusPoints}!`, {
      fontFamily: 'monospace',
      fontSize: '14px',
      color: '#ffff00',
    }).setOrigin(0.5)

    this.tweens.add({
      targets: bonusText,
      y: y - 50,
      alpha: 0,
      duration: 800,
      ease: 'Power2',
      onComplete: () => bonusText.destroy(),
    })
  }

  private onBulletHitPlayer(
    bulletObj: Phaser.Types.Physics.Arcade.GameObjectWithBody | Phaser.Tilemaps.Tile,
    _playerObj: Phaser.Types.Physics.Arcade.GameObjectWithBody | Phaser.Tilemaps.Tile
  ): void {
    if (this.isPlayerInvincible) return

    const bullet = bulletObj as Phaser.GameObjects.Rectangle
    if (!bullet.active) return

    bullet.destroy()

    // Check if shield absorbs the hit
    if (this.powerUpManager.absorbHit()) {
      // Shield protected the player - create visual effect
      this.createShieldHitEffect()
      return
    }

    this.playerHit()
  }

  private createShieldHitEffect(): void {
    // Flash the shield
    if (this.shieldGraphics) {
      this.tweens.add({
        targets: this.shieldGraphics,
        alpha: { from: 1, to: 0.2 },
        duration: 100,
        yoyo: true,
        repeat: 2,
      })
    }

    // Show "BLOCKED" text
    const blockedText = this.add.text(
      this.player.x,
      this.player.y - 40,
      'BLOCKED!',
      {
        fontFamily: 'monospace',
        fontSize: '14px',
        color: '#00aaff',
      }
    ).setOrigin(0.5)

    this.tweens.add({
      targets: blockedText,
      y: this.player.y - 70,
      alpha: 0,
      duration: 600,
      ease: 'Power2',
      onComplete: () => blockedText.destroy(),
    })
  }

  private onAlienHitPlayer(
    _alienObj: Phaser.Types.Physics.Arcade.GameObjectWithBody | Phaser.Tilemaps.Tile,
    _playerObj: Phaser.Types.Physics.Arcade.GameObjectWithBody | Phaser.Tilemaps.Tile
  ): void {
    if (this.isPlayerInvincible) return
    this.gameOver() // Instant game over if alien touches player
  }

  private playerHit(): void {
    this.lives--
    this.updateLivesUI()

    // Announce life lost to screen readers
    accessibilityAnnouncer.announceLifeLost(this.lives)
    accessibilityAnnouncer.updateGameState(this.score, this.lives)

    // Audio and visual feedback for player hit
    audioManager.playPlayerDeathSound()
    this.createExplosion(this.player.x, this.player.y)

    if (this.lives <= 0) {
      this.gameOver()
    } else {
      // Temporary invincibility and visual feedback
      this.isPlayerInvincible = true
      this.player.setAlpha(0.5)

      // Announce invincibility to screen readers
      accessibilityAnnouncer.announceInvincibility(true)

      // Blink effect
      this.tweens.add({
        targets: this.player,
        alpha: { from: 0.3, to: 1 },
        duration: 200,
        repeat: 5,
        yoyo: true,
        onComplete: () => {
          this.player.setAlpha(1)
          this.isPlayerInvincible = false
        },
      })
    }
  }

  private createExplosion(x: number, y: number): void {
    // Simple particle-like explosion using rectangles
    for (let i = 0; i < 8; i++) {
      const particle = this.add.rectangle(
        x,
        y,
        4,
        4,
        0xffffff
      )
      const angle = (i / 8) * Math.PI * 2

      this.tweens.add({
        targets: particle,
        x: x + Math.cos(angle) * 50,
        y: y + Math.sin(angle) * 50,
        alpha: 0,
        scale: 0,
        duration: 300,
        ease: 'Power2',
        onComplete: () => particle.destroy(),
      })
    }
  }

  // ========================================
  // Win/Lose Conditions
  // ========================================

  private checkWinCondition(): void {
    const remainingAliens = this.aliens
      .getChildren()
      .filter((a) => a.active).length

    if (remainingAliens === 0) {
      // Track wave completion for score validation
      LeaderboardManager.recordWaveCompleted()
      this.win()
    }
  }

  private async win(): Promise<void> {
    this.isPaused = true
    audioManager.playVictorySound()

    // Record game end for difficulty tracking
    difficultyManager.recordGameEnd(true)

    // Record game end for event progress
    eventManager.onGameEnd(this.score)

    // Submit score to global leaderboard
    const result = await LeaderboardManager.submitScore(this.score)
    this.globalRank = result.rank

    // Announce victory to screen readers
    accessibilityAnnouncer.announceVictory(this.score)

    // Stop countdown timer
    if (this.countdownTimer) {
      this.countdownTimer.destroy()
    }

    this.showGameOverScreen(true)
  }

  private async gameOver(): Promise<void> {
    this.isGameOver = true
    this.player.setVisible(false)
    audioManager.playGameOverSound()

    // Record game end for difficulty tracking
    difficultyManager.recordGameEnd(false)

    // Record game end for event progress
    eventManager.onGameEnd(this.score)

    // Submit score to global leaderboard
    const result = await LeaderboardManager.submitScore(this.score)
    this.globalRank = result.rank

    // Announce game over to screen readers
    accessibilityAnnouncer.announceGameOver(this.score)

    // Stop countdown timer
    if (this.countdownTimer) {
      this.countdownTimer.destroy()
    }

    this.showGameOverScreen(false)
  }

  private showGameOverScreen(isWin: boolean): void {
    // Create the game over leaderboard overlay
    const leaderboardContainer = createGameOverLeaderboard(
      this,
      this.score,
      isWin,
      this.globalRank
    )

    // Restart instructions at bottom
    const restartMessage = this.useTouchControls
      ? 'Tap anywhere to play again'
      : 'Press SPACE to play again'
    const restartText = this.add
      .text(GAME_WIDTH / 2, GAME_HEIGHT - 40, restartMessage, {
        fontFamily: 'monospace',
        fontSize: UI_CONFIG.fontSize.instructions,
        color: UI_CONFIG.colors.primary,
      })
      .setOrigin(0.5)
      .setInteractive()

    leaderboardContainer.add(restartText)

    // Blink restart text
    this.tweens.add({
      targets: restartText,
      alpha: { from: 1, to: 0.3 },
      duration: 500,
      yoyo: true,
      repeat: -1,
    })

    // Create invisible overlay for touch restart
    const touchOverlay = this.add.rectangle(
      GAME_WIDTH / 2,
      GAME_HEIGHT / 2,
      GAME_WIDTH,
      GAME_HEIGHT,
      0x000000,
      0
    ).setInteractive()

    leaderboardContainer.add(touchOverlay)

    // Listen for restart
    this.input.keyboard?.once('keydown-SPACE', () => {
      this.restartGame()
    })

    // Touch restart support
    touchOverlay.once('pointerdown', () => {
      this.restartGame()
    })
  }

  private restartGame(): void {
    // Clear accessibility announcements
    accessibilityAnnouncer.clear()

    // Clean up and restart scene
    this.scene.restart()
  }

  private announceGameStart(): void {
    // Announce game start to screen readers
    accessibilityAnnouncer.announceGameStart()
    accessibilityAnnouncer.updateGameState(this.score, this.lives)
  }

  // ========================================
  // Pause Functionality
  // ========================================

  private togglePause(): void {
    if (this.isGameOver) return

    this.isPaused = !this.isPaused

    // Announce pause state change to screen readers
    accessibilityAnnouncer.announcePause(this.isPaused)

    if (this.isPaused) {
      this.showPauseOverlay()
    } else {
      this.hidePauseOverlay()
    }
  }

  private showPauseOverlay(): void {
    // Create semi-transparent dark overlay
    const overlay = this.add.rectangle(
      GAME_WIDTH / 2,
      GAME_HEIGHT / 2,
      GAME_WIDTH,
      GAME_HEIGHT,
      0x000000,
      0.7
    )

    // PAUSED text
    const pausedText = this.add
      .text(GAME_WIDTH / 2, GAME_HEIGHT / 2 - 40, 'PAUSED', {
        fontFamily: 'monospace',
        fontSize: '48px',
        color: UI_CONFIG.colors.primary,
      })
      .setOrigin(0.5)

    // Resume instructions
    const resumeText = this.add
      .text(GAME_WIDTH / 2, GAME_HEIGHT / 2 + 30, 'Press ESC to resume', {
        fontFamily: 'monospace',
        fontSize: UI_CONFIG.fontSize.instructions,
        color: UI_CONFIG.colors.white,
      })
      .setOrigin(0.5)

    // Blink effect on resume text
    this.tweens.add({
      targets: resumeText,
      alpha: { from: 1, to: 0.3 },
      duration: 500,
      yoyo: true,
      repeat: -1,
    })

    // Store in container for easy removal
    this.pauseOverlay = this.add.container(0, 0, [
      overlay,
      pausedText,
      resumeText,
    ])
  }

  private hidePauseOverlay(): void {
    if (this.pauseOverlay) {
      this.pauseOverlay.destroy()
      this.pauseOverlay = undefined
    }
  }

  // ========================================
  // UI
  // ========================================

  private createUI(): void {
    // Title
    this.add
      .text(UI_CONFIG.titlePosition.x, UI_CONFIG.titlePosition.y, 'SPACE INVADERS 2030', {
        fontFamily: 'monospace',
        fontSize: UI_CONFIG.fontSize.title,
        color: UI_CONFIG.colors.primary,
      })
      .setOrigin(0.5)

    // Score
    this.scoreText = this.add.text(
      UI_CONFIG.scorePosition.x,
      UI_CONFIG.scorePosition.y + 40,
      `SCORE: ${this.score}`,
      {
        fontFamily: 'monospace',
        fontSize: UI_CONFIG.fontSize.score,
        color: UI_CONFIG.colors.white,
      }
    )

    // Lives
    this.livesText = this.add
      .text(
        UI_CONFIG.livesPosition.x,
        UI_CONFIG.livesPosition.y + 40,
        `LIVES: ${this.lives}`,
        {
          fontFamily: 'monospace',
          fontSize: UI_CONFIG.fontSize.score,
          color: UI_CONFIG.colors.white,
        }
      )
      .setOrigin(1, 0)

    // Skill level indicator
    const skillInfo = difficultyManager.getSkillDisplay()
    this.add
      .text(
        GAME_WIDTH / 2,
        UI_CONFIG.scorePosition.y + 40,
        `SKILL: ${skillInfo.level.toUpperCase()}`,
        {
          fontFamily: 'monospace',
          fontSize: '14px',
          color: this.getSkillColor(skillInfo.level),
        }
      )
      .setOrigin(0.5, 0)

    // Controls hint at bottom (show different text for touch vs keyboard)
    const controlsText = this.useTouchControls
      ? 'USE ON-SCREEN CONTROLS   M MUTE'
      : '← → MOVE   SPACE FIRE   M MUTE   ESC PAUSE'
    this.add
      .text(GAME_WIDTH / 2, GAME_HEIGHT - 15, controlsText, {
        fontFamily: 'monospace',
        fontSize: '14px',
        color: UI_CONFIG.colors.primary,
      })
      .setOrigin(0.5)

    // Mute button (top right corner)
    this.muteButton = this.add
      .text(GAME_WIDTH - 10, 70, this.getMuteButtonText(), {
        fontFamily: 'monospace',
        fontSize: '16px',
        color: UI_CONFIG.colors.primary,
        backgroundColor: '#222222',
        padding: { x: 8, y: 4 },
      })
      .setOrigin(1, 0)
      .setInteractive({ useHandCursor: true })
      .on('pointerdown', () => this.toggleMute())
      .on('pointerover', () => this.muteButton.setColor('#ffffff'))
      .on('pointerout', () => this.muteButton.setColor(UI_CONFIG.colors.primary))

    // Keyboard shortcut for mute (M key)
    this.input.keyboard?.addKey(Phaser.Input.Keyboard.KeyCodes.M)
      .on('down', () => this.toggleMute())
  }

  private getMuteButtonText(): string {
    return audioManager.getMuted() ? '🔇 MUTED' : '🔊 SOUND'
  }

  private toggleMute(): void {
    audioManager.toggleMute()
    this.muteButton.setText(this.getMuteButtonText())
  }

  private updateScoreUI(): void {
    this.scoreText.setText(`SCORE: ${this.score}`)
  }

  private updateLivesUI(): void {
    this.livesText.setText(`LIVES: ${this.lives}`)
    if (this.lives <= 1) {
      this.livesText.setColor(UI_CONFIG.colors.danger)
    }
  }

  private getSkillColor(level: string): string {
    const colors: Record<string, string> = {
      Beginner: '#4CAF50', // Green
      Intermediate: '#2196F3', // Blue
      Advanced: '#FF9800', // Orange
      Expert: '#F44336', // Red
    }
    return colors[level] || UI_CONFIG.colors.white
  }

  // ========================================
  // Event System UI
  // ========================================

  private createEventUI(): void {
    if (!this.currentEvent) return

    // Event banner at top
    const bannerY = 65
    const bannerColor = eventThemeManager.getUIPrimaryColor()

    this.eventBannerText = this.add.text(
      GAME_WIDTH / 2,
      bannerY,
      `${this.currentEvent.icon} ${this.currentEvent.name}`,
      {
        fontFamily: 'monospace',
        fontSize: '12px',
        color: bannerColor,
      }
    ).setOrigin(0.5)

    // Bonus multiplier text
    if (this.currentEvent.bonusMultiplier) {
      const multiplierText = this.add.text(
        GAME_WIDTH / 2,
        bannerY + 14,
        this.currentEvent.bonusMultiplier.description,
        {
          fontFamily: 'monospace',
          fontSize: '10px',
          color: '#ffff00',
        }
      ).setOrigin(0.5)

      // Pulse animation for bonus text
      this.tweens.add({
        targets: multiplierText,
        alpha: { from: 1, to: 0.6 },
        duration: 800,
        yoyo: true,
        repeat: -1,
      })
    }

    // Countdown timer
    this.eventCountdownText = this.add.text(
      GAME_WIDTH - 10,
      bannerY,
      '',
      {
        fontFamily: 'monospace',
        fontSize: '10px',
        color: '#888888',
      }
    ).setOrigin(1, 0)

    // Update countdown every second
    this.updateEventCountdown()
    this.countdownTimer = this.time.addEvent({
      delay: 1000,
      callback: this.updateEventCountdown,
      callbackScope: this,
      loop: true,
    })
  }

  private updateEventCountdown(): void {
    if (!this.eventCountdownText || !this.currentEvent) return

    const countdown = eventManager.getCountdownString()
    this.eventCountdownText.setText(countdown)

    // Check if event has ended
    if (!eventManager.checkEventStatus()) {
      this.currentEvent = null
      if (this.eventBannerText) {
        this.eventBannerText.setText('Event Ended')
        this.eventBannerText.setColor('#888888')
      }
    }
  }

  private showAchievementToast(achievement: EventAchievement): void {
    // Remove existing toast if present
    if (this.achievementToast) {
      this.achievementToast.destroy()
    }

    const toastX = GAME_WIDTH / 2
    const toastY = 100

    // Background
    const bg = this.add.rectangle(0, 0, 280, 60, 0x000000, 0.9)
    bg.setStrokeStyle(2, 0xffd700)

    // Achievement icon and text
    const iconText = this.add.text(-120, -15, achievement.icon, {
      fontFamily: 'monospace',
      fontSize: '24px',
    })

    const titleText = this.add.text(-85, -18, 'Achievement Unlocked!', {
      fontFamily: 'monospace',
      fontSize: '10px',
      color: '#ffd700',
    })

    const nameText = this.add.text(-85, 0, achievement.name, {
      fontFamily: 'monospace',
      fontSize: '14px',
      color: '#ffffff',
    })

    const descText = this.add.text(-85, 16, achievement.description, {
      fontFamily: 'monospace',
      fontSize: '9px',
      color: '#aaaaaa',
    })

    // Create container
    this.achievementToast = this.add.container(toastX, toastY - 80, [
      bg, iconText, titleText, nameText, descText,
    ])

    // Animate in
    this.tweens.add({
      targets: this.achievementToast,
      y: toastY,
      duration: 500,
      ease: 'Back.easeOut',
    })

    // Animate out after delay
    this.time.delayedCall(4000, () => {
      if (this.achievementToast) {
        this.tweens.add({
          targets: this.achievementToast,
          y: toastY - 80,
          alpha: 0,
          duration: 500,
          ease: 'Power2',
          onComplete: () => {
            if (this.achievementToast) {
              this.achievementToast.destroy()
              this.achievementToast = undefined
            }
          },
        })
      }
    })
  }

  // ========================================
  // Power-Up System UI
  // ========================================

  private createPowerUpUI(): void {
    // Container for active power-up indicators (bottom left)
    this.powerUpUIContainer = this.add.container(10, GAME_HEIGHT - 50)
  }

  private updatePowerUpUI(): void {
    if (!this.powerUpUIContainer) return

    const activePowerUps = this.powerUpManager.getActivePowerUps()

    // Clear existing UI elements that are no longer active
    for (const [type, textElement] of this.powerUpTimerTexts) {
      const isStillActive = activePowerUps.some(p => p.type === type)
      if (!isStillActive) {
        textElement.destroy()
        this.powerUpTimerTexts.delete(type)
      }
    }

    // Update or create UI elements for active power-ups
    activePowerUps.forEach((powerUp: ActivePowerUp, index: number) => {
      const remaining = this.powerUpManager.getRemainingTime(powerUp.type)
      const seconds = Math.ceil(remaining / 1000)
      const typeConfig = POWERUP_CONFIG.types[powerUp.type]

      let textElement = this.powerUpTimerTexts.get(powerUp.type)

      if (!textElement) {
        // Create new UI element
        textElement = this.add.text(0, 0, '', {
          fontFamily: 'monospace',
          fontSize: '12px',
          color: '#ffffff',
          backgroundColor: '#000000',
          padding: { x: 4, y: 2 },
        })
        this.powerUpUIContainer!.add(textElement)
        this.powerUpTimerTexts.set(powerUp.type, textElement)

        // Animate appearance
        textElement.setAlpha(0)
        this.tweens.add({
          targets: textElement,
          alpha: 1,
          duration: 200,
        })
      }

      // Update position and text
      textElement.setPosition(0, -index * 18)
      textElement.setText(`${typeConfig.icon} ${typeConfig.name} ${seconds}s`)

      // Color based on remaining time
      if (seconds <= 2) {
        textElement.setColor('#ff4444') // Red when expiring
        // Pulse effect when low
        if (!textElement.getData('pulsing')) {
          textElement.setData('pulsing', true)
          this.tweens.add({
            targets: textElement,
            alpha: { from: 1, to: 0.5 },
            duration: 200,
            yoyo: true,
            repeat: -1,
          })
        }
      } else if (seconds <= 5) {
        textElement.setColor('#ffaa00') // Orange warning
      } else {
        textElement.setColor('#ffffff') // White normal
      }
    })
  }

  private updateShieldVisual(): void {
    const hasShield = this.powerUpManager.isActive(PowerUpType.SHIELD)

    if (hasShield && !this.shieldGraphics) {
      // Create shield visual around player
      this.shieldGraphics = this.add.graphics()
      this.drawShield()
    } else if (!hasShield && this.shieldGraphics) {
      // Remove shield visual
      this.shieldGraphics.destroy()
      this.shieldGraphics = undefined
    }

    // Update shield position to follow player
    if (this.shieldGraphics && this.player.active) {
      this.shieldGraphics.clear()
      this.drawShield()
    }
  }

  private drawShield(): void {
    if (!this.shieldGraphics || !this.player.active) return

    const x = this.player.x
    const y = this.player.y
    const radius = 35

    // Outer glow
    this.shieldGraphics.lineStyle(4, 0x00aaff, 0.3)
    this.shieldGraphics.strokeCircle(x, y, radius + 5)

    // Main shield
    this.shieldGraphics.lineStyle(2, 0x00aaff, 0.8)
    this.shieldGraphics.strokeCircle(x, y, radius)

    // Inner highlight
    this.shieldGraphics.lineStyle(1, 0x88ddff, 0.5)
    this.shieldGraphics.strokeCircle(x, y, radius - 3)
  }
}
